import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { ArrowLeft, Plus, Percent, Euro, Mail } from 'lucide-react';
import promoCodeStore from '../../store/promo-code-store';
import promoCodeViewModel from '../../view-model/professional/promo-code/promo-code-view-model';
import companyStore from '../../store/company-store';
import PromoCodeFormModal, { PromoCodeFormData } from '../../components/professional/promo-code-form-modal';
import { PromoCode } from '../../model/professional/promo-code-model';
import ConfirmationModal from '../../components/ui/confirmation-modal';
import serviceViewModel from '../../view-model/professional/company/service-view-model';
import PromoCodeEmailModal from '../../components/professional/promo-code-email-modal';

interface PromoCodesPageProps {}

const PromoCodesPage: React.FC<PromoCodesPageProps> = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const vm = promoCodeViewModel;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [promoCodeToDelete, setPromoCodeToDelete] = useState<string | null>(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [promoCodeToEmail, setPromoCodeToEmail] = useState<PromoCode | null>(null);

  const company = companyStore.companies.find(c => c.id === companyId);

  useEffect(() => {
    if (companyId) {
      // Définir l'entreprise sélectionnée dans le store
      companyStore.setIdCompanySelectedDefault(companyId);

      // Charger les données
      vm.loadPromoCodesByCompany(companyId);
      vm.checkCanCreatePromoCode(companyId);
      serviceViewModel.getServices(); // Charger les services de l'entreprise
    }
  }, [companyId]);

  const handleBack = () => {
    navigate(`/${i18n.language}/provider/companies`);
  };

  const handleCreatePromoCode = () => {
    setEditingPromoCode(null);
    setIsModalOpen(true);
  };

  const handleEditPromoCode = (promoCodeId: string) => {
    const promoCode = promoCodeStore.promoCodes.find(pc => pc.id === promoCodeId);
    if (promoCode) {
      setEditingPromoCode(promoCode);
      setIsModalOpen(true);
    }
  };

  const handleModalSubmit = async (data: PromoCodeFormData) => {
    if (!companyId) return;

    try {
      if (editingPromoCode) {
        // Mise à jour
        await vm.updatePromoCode(editingPromoCode.id, {
          code: data.code,
          discountType: data.discountType,
          discountValue: data.discountValue,
          validFrom: new Date(data.validFrom),
          validUntil: data.validUntil ? new Date(data.validUntil) : undefined,
          applicableServiceIds: data.applicableServiceIds,
          assignedClientIds: data.assignedClientIds,
          description: data.description,
          internalNotes: data.internalNotes,
        });
      } else {
        // Création
        await vm.createPromoCode(companyId, {
          code: data.code,
          discountType: data.discountType,
          discountValue: data.discountValue,
          validFrom: new Date(data.validFrom),
          validUntil: data.validUntil ? new Date(data.validUntil) : undefined,
          applicableServiceIds: data.applicableServiceIds,
          assignedClientIds: data.assignedClientIds,
          description: data.description,
          internalNotes: data.internalNotes,
        });
      }

      setIsModalOpen(false);
      setEditingPromoCode(null);
      await vm.loadPromoCodesByCompany(companyId);
    } catch (error) {
      console.error('Error submitting promo code:', error);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingPromoCode(null);
  };

  const handleTogglePromoCode = async (promoCodeId: string, isActive: boolean) => {
    if (companyId) {
      await vm.togglePromoCode(promoCodeId, !isActive);
      await vm.loadPromoCodesByCompany(companyId);
    }
  };

  const handleDeletePromoCode = (promoCodeId: string) => {
    setPromoCodeToDelete(promoCodeId);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (promoCodeToDelete && companyId) {
      await vm.deletePromoCode(promoCodeToDelete);
      await vm.loadPromoCodesByCompany(companyId);
      setPromoCodeToDelete(null);
    }
    setIsDeleteConfirmOpen(false);
  };

  const handleSendEmail = (promoCodeId: string) => {
    const promoCode = promoCodeStore.promoCodes.find(pc => pc.id === promoCodeId);
    if (promoCode) {
      setPromoCodeToEmail(promoCode);
      setIsEmailModalOpen(true);
    }
  };

  const handleSendEmailToClients = async (clientIds: string[]) => {
    if (!promoCodeToEmail) return;

    try {
      const success = await vm.sendPromoCodeEmail(promoCodeToEmail.id, clientIds);

      if (success) {
        // Reload promo codes to update email stats
        if (companyId) {
          await vm.loadPromoCodesByCompany(companyId);
        }
      }

      setIsEmailModalOpen(false);
      setPromoCodeToEmail(null);
    } catch (error) {
      console.error('Error sending emails:', error);
    }
  };

  const promoCodes = promoCodeStore.getPromoCodes;
  const canCreate = promoCodeStore.getCanCreate;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('promoCode.page.title', 'Codes Promo')}
            </h1>
            {company && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {company.businessName}
              </p>
            )}
          </div>
        </div>

        {canCreate ? (
          <button
            onClick={handleCreatePromoCode}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Plus size={20} />
            {t('promoCode.create', 'Créer un code')}
          </button>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('promoCode.limitReached', 'Limite de codes atteinte. Passez à un plan supérieur.')}
          </div>
        )}
      </div>

      {/* Liste des codes promos */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {promoCodes.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
              <Percent size={24} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t('promoCode.noCode', 'Aucun code promo')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('promoCode.noCodeDescription', 'Créez votre premier code promo pour attirer de nouveaux clients')}
            </p>
            {canCreate && (
              <button
                onClick={handleCreatePromoCode}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                <Plus size={20} />
                {t('promoCode.createFirst', 'Créer mon premier code')}
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {promoCodes.map((promoCode) => (
              <div
                key={promoCode.id}
                className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 bg-primary/10 text-primary font-mono font-bold text-lg rounded">
                        {promoCode.code}
                      </span>
                      {promoCode.isActive ? (
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs font-medium rounded">
                          {t('promoCode.active', 'Actif')}
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400 text-xs font-medium rounded">
                          {t('promoCode.inactive', 'Inactif')}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {promoCode.discountType === 'percentage' ? (
                        <>
                          <Percent size={20} className="text-primary" />
                          -{promoCode.discountValue}%
                        </>
                      ) : (
                        <>
                          <Euro size={20} className="text-primary" />
                          -{promoCode.discountValue}€
                        </>
                      )}
                    </div>

                    {promoCode.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {promoCode.description}
                      </p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span>
                        {t('promoCode.validFrom', 'Valide du')} {new Date(promoCode.validFrom).toLocaleDateString()}
                        {promoCode.validUntil && ` ${t('promoCode.to', 'au')} ${new Date(promoCode.validUntil).toLocaleDateString()}`}
                      </span>
                      {promoCode.usedByClients && promoCode.usedByClients.length > 0 && (
                        <span>
                          {t('promoCode.usedCount', 'Utilisé {{count}} fois', { count: promoCode.usedByClients.length })}
                        </span>
                      )}
                    </div>

                    {promoCode.applicableServiceIds && promoCode.applicableServiceIds.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {t('promoCode.applicableToServices', 'Applicable à {{count}} service(s)', { count: promoCode.applicableServiceIds.length })}
                        </span>
                      </div>
                    )}

                    {promoCode.emailsSentCount > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-blue-600 dark:text-blue-400">
                          {t('promoCode.emailsSent', '{{count}} email(s) envoyé(s)', { count: promoCode.emailsSentCount })}
                          {promoCode.emailsSentAt && ` ${t('promoCode.on', 'le')} ${new Date(promoCode.emailsSentAt).toLocaleDateString()}`}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSendEmail(promoCode.id)}
                      className="px-3 py-1 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors flex items-center gap-1"
                    >
                      <Mail size={16} />
                      {t('promoCode.sendEmail', 'Envoyer')}
                    </button>
                    <button
                      onClick={() => handleTogglePromoCode(promoCode.id, promoCode.isActive)}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        promoCode.isActive
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                      }`}
                    >
                      {promoCode.isActive ? t('promoCode.deactivate', 'Désactiver') : t('promoCode.activate', 'Activer')}
                    </button>
                    <button
                      onClick={() => handleEditPromoCode(promoCode.id)}
                      className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                    >
                      {t('promoCode.edit', 'Modifier')}
                    </button>
                    <button
                      onClick={() => handleDeletePromoCode(promoCode.id)}
                      className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                    >
                      {t('promoCode.delete', 'Supprimer')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de création/édition */}
      <PromoCodeFormModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        companyId={companyId || ''}
        promoCode={editingPromoCode}
      />

      {/* Modal d'envoi d'emails */}
      {promoCodeToEmail && (
        <PromoCodeEmailModal
          isOpen={isEmailModalOpen}
          onClose={() => {
            setIsEmailModalOpen(false);
            setPromoCodeToEmail(null);
          }}
          onSend={handleSendEmailToClients}
          promoCode={promoCodeToEmail}
          companyId={companyId || ''}
        />
      )}

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title={String(t('promoCode.deleteTitle', 'Supprimer le code promo'))}
        message={String(t('promoCode.deleteMessage', 'Êtes-vous sûr de vouloir supprimer ce code promo ? Cette action est irréversible.'))}
        type="warning"
        confirmText={String(t('common.delete', 'Supprimer'))}
        cancelText={String(t('common.cancel', 'Annuler'))}
      />
    </div>
  );
});

export default PromoCodesPage;
